import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { formatDeShort } from "@/lib/date";
import {
  MessageCircle,
  Send,
  Paperclip,
  Users,
  UserPlus,
  X,
  Download,
  FileText,
  Image as ImageIcon,
  Circle,
  SmilePlus,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 5000;
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const REACTION_EMOJIS = ["👍", "❤️", "✅", "🙌", "🎉", "😂", "😮", "🔥"];

const ROLE_BADGE = {
  admin: { label: "Admin", cls: "bg-slate-900 text-white" },
  manager: { label: "Manager", cls: "bg-slate-700 text-white" },
  coach: { label: "Coach", cls: "bg-[#5b2eff] text-white" },
  lecturer: { label: "Dozent", cls: "bg-[#0a788c] text-white" },
  participant: { label: "Teilnehmer", cls: "bg-emerald-600 text-white" },
};

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch (_) {
    return "";
  }
}
function formatDay(iso) {
  if (!iso) return "";
  return formatDeShort(iso.slice(0, 10));
}

export default function ChatPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [roomType, setRoomType] = useState("group");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState("");
  const [pendingFile, setPendingFile] = useState(null); // {filename, content_type, size, data_base64, is_image}
  const [addDMOpen, setAddDMOpen] = useState(false);
  const [dmClassId, setDmClassId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadRooms = async () => {
    try {
      const r = await api.get("/weiterbildung/chat/rooms");
      setRooms(r.data.rooms || []);
      if (!activeRoomId && r.data.rooms?.length) {
        setActiveRoomId(r.data.rooms[0].room_id);
        setRoomType(r.data.rooms[0].room_type);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  const loadMessages = async (rid) => {
    if (!rid) return;
    try {
      const r = await api.get(`/weiterbildung/chat/rooms/${rid}/messages`);
      setMessages(r.data.messages || []);
      setRoomType(r.data.room_type);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };
  const markRead = async (rid) => {
    try { await api.post(`/weiterbildung/chat/rooms/${rid}/read`); } catch (_) { /* ignore */ }
  };

  // initial load
  useEffect(() => { loadRooms(); }, []);

  // when active room changes
  useEffect(() => {
    if (!activeRoomId) return;
    loadMessages(activeRoomId).then(() => {
      markRead(activeRoomId);
      loadRooms();
    });
  }, [activeRoomId]);

  // polling
  useEffect(() => {
    if (!activeRoomId) return;
    const iv = setInterval(async () => {
      const r = await api.get(`/weiterbildung/chat/rooms/${activeRoomId}/messages`);
      const newMsgs = r.data.messages || [];
      setMessages((prev) => {
        // only update if changed to prevent flicker
        if (prev.length === newMsgs.length && prev[prev.length - 1]?.message_id === newMsgs[newMsgs.length - 1]?.message_id) {
          return prev;
        }
        return newMsgs;
      });
      // refresh rooms + read
      loadRooms();
      markRead(activeRoomId);
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [activeRoomId]);

  // auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const activeRoom = useMemo(() => rooms.find((r) => r.room_id === activeRoomId) || null, [rooms, activeRoomId]);

  const send = async () => {
    if (sending) return;
    if (!content.trim() && !pendingFile) return;
    setSending(true);
    try {
      const body = { content: content.trim() };
      if (pendingFile) {
        body.attachment = {
          filename: pendingFile.filename,
          content_type: pendingFile.content_type,
          data_base64: pendingFile.data_base64,
        };
      }
      await api.post(`/weiterbildung/chat/rooms/${activeRoomId}/messages`, body);
      setContent("");
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadMessages(activeRoomId);
      loadRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`Datei zu groß (max ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB).`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = String(dataUrl).split(",", 2)[1] || "";
      setPendingFile({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        size: file.size,
        data_base64: b64,
        is_image: (file.type || "").startsWith("image/"),
      });
    };
    reader.readAsDataURL(file);
  };

  const openDMDialog = async () => {
    if (!activeRoom?.class_id) return;
    setDmClassId(activeRoom.class_id);
    try {
      const r = await api.get(`/weiterbildung/chat/rooms/${activeRoomId}/members`);
      setCandidates(r.data.members || []);
      setAddDMOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };
  const startDM = async (otherUserId) => {
    try {
      const r = await api.post(`/weiterbildung/chat/rooms/dm`, {
        class_id: dmClassId,
        other_user_id: otherUserId,
      });
      setAddDMOpen(false);
      await loadRooms();
      setActiveRoomId(r.data.room_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading) return <div className="p-8 text-xs text-slate-500">Lädt Chat…</div>;
  if (rooms.length === 0) {
    return (
      <div className="p-12 max-w-2xl">
        <MessageCircle size={40} strokeWidth={1.5} className="text-slate-400 mb-4" />
        <h1 className="font-heading text-4xl font-light tracking-tighter">Kein Chat verfügbar</h1>
        <p className="text-sm text-slate-500 mt-3">
          Du bist aktuell keiner aktiven Klasse zugeordnet. Sobald ein Admin dich einer Klasse hinzufügt,
          erscheint hier automatisch ein Klassen-Chat.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-0px)] flex bg-white" data-testid="chat-page">
      {/* Left: rooms list */}
      <aside className="w-72 border-r border-slate-200 flex flex-col shrink-0">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Portal</div>
          <div className="font-heading text-2xl font-light tracking-tighter">Chat</div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="chat-room-list">
          {rooms.map((r) => {
            const isActive = r.room_id === activeRoomId;
            const label =
              r.room_type === "group"
                ? `#${r.class_name || r.name || "Klasse"}`
                : (r.other_members?.[0]?.name || "1:1 Chat");
            const roleTag = r.room_type === "dm" ? r.other_members?.[0]?.role : null;
            const roleBadge = ROLE_BADGE[roleTag];
            return (
              <button
                key={r.room_id}
                onClick={() => setActiveRoomId(r.room_id)}
                data-testid={`chat-room-${r.room_id}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex flex-col gap-1 ${isActive ? "bg-slate-100 border-l-2 border-l-slate-900" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.room_type === "group" ? <Users size={14} strokeWidth={1.5} className="text-slate-500 shrink-0" /> : <Circle size={8} className="text-emerald-500 fill-emerald-500 shrink-0" />}
                    <span className="text-sm font-medium text-slate-900 truncate">{label}</span>
                  </div>
                  {r.unread > 0 && (
                    <span className="text-[10px] font-bold bg-[#ff3d7f] text-white px-1.5 py-0.5 rounded-full shrink-0" data-testid={`unread-${r.room_id}`}>
                      {r.unread}
                    </span>
                  )}
                </div>
                {roleBadge && (
                  <span className={`text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 self-start ${roleBadge.cls}`}>
                    {roleBadge.label}
                  </span>
                )}
                {r.last_message && (
                  <div className="text-[11px] text-slate-500 truncate">
                    <span className="font-medium text-slate-600">{r.last_message.sender_name?.split(" ")[0]}:</span>{" "}
                    {r.last_message.has_attachment ? "📎 Anhang " : ""}{r.last_message.content || ""}
                  </div>
                )}
                {r.last_message?.created_at && (
                  <div className="text-[9px] text-slate-400 font-mono uppercase tracking-overline">{formatDay(r.last_message.created_at)} · {formatTime(r.last_message.created_at)}</div>
                )}
              </button>
            );
          })}
        </div>
        {activeRoom?.class_id && (
          <div className="border-t border-slate-200 p-3">
            <button
              onClick={openDMDialog}
              data-testid="new-dm-btn"
              className="w-full text-xs border border-slate-300 hover:bg-slate-900 hover:text-white px-3 py-2 flex items-center justify-center gap-2"
            >
              <UserPlus size={12} strokeWidth={1.5} /> Neue Direkt-Unterhaltung
            </button>
          </div>
        )}
      </aside>

      {/* Right: messages */}
      <main className="flex-1 flex flex-col min-w-0">
        {activeRoom && (
          <>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">
                  {activeRoom.measure_number} · {activeRoom.room_type === "group" ? "Klassen-Gruppenchat" : "Direkt-Chat"}
                </div>
                <div className="font-heading text-2xl font-light tracking-tighter truncate">
                  {activeRoom.room_type === "group"
                    ? `#${activeRoom.class_name}`
                    : activeRoom.other_members?.[0]?.name || "1:1 Chat"}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-[#fafbfd]" data-testid="chat-messages">
              {messages.length === 0 ? (
                <div className="text-center text-slate-400 italic text-xs py-16">Noch keine Nachrichten. Sei die/der Erste!</div>
              ) : (
                messages.map((m, idx) => {
                  const isMe = m.sender_user_id === user?.user_id;
                  const prev = messages[idx - 1];
                  const showDate = !prev || (prev.created_at || "").slice(0, 10) !== (m.created_at || "").slice(0, 10);
                  const roleBadge = ROLE_BADGE[m.sender_role];
                  return (
                    <React.Fragment key={m.message_id}>
                      {showDate && (
                        <div className="flex justify-center my-4">
                          <span className="text-[10px] font-mono uppercase tracking-overline bg-white border border-slate-200 px-3 py-1 text-slate-500">
                            {formatDay(m.created_at)}
                          </span>
                        </div>
                      )}
                      <MessageBubble m={m} isMe={isMe} roleBadge={roleBadge} />
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-slate-200 px-4 py-3 bg-white">
              {pendingFile && (
                <div className="mb-3 flex items-center gap-3 border border-slate-200 bg-slate-50 px-3 py-2">
                  {pendingFile.is_image ? <ImageIcon size={16} strokeWidth={1.5} className="text-slate-500" /> : <FileText size={16} strokeWidth={1.5} className="text-slate-500" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900 truncate">{pendingFile.filename}</div>
                    <div className="text-[10px] font-mono text-slate-500">{(pendingFile.size / 1024).toFixed(1)} KB · {pendingFile.content_type}</div>
                  </div>
                  <button onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value=""; }} className="p-1 hover:bg-slate-200" data-testid="remove-attachment-btn">
                    <X size={14} strokeWidth={1.5} />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <label className="p-3 border border-slate-300 hover:bg-slate-100 cursor-pointer" data-testid="attach-file-btn">
                  <Paperclip size={16} strokeWidth={1.5} />
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={onFileChosen}
                    accept="image/*,application/pdf,.doc,.docx,.txt"
                    className="hidden"
                    data-testid="chat-file-input"
                  />
                </label>
                <textarea
                  data-testid="chat-input"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder="Nachricht schreiben… (Enter zum Senden, Shift+Enter für neue Zeile)"
                  className="flex-1 border border-slate-300 focus:border-slate-900 outline-none px-3 py-2.5 text-sm resize-none min-h-[42px] max-h-[120px]"
                />
                <button
                  onClick={send}
                  disabled={sending || (!content.trim() && !pendingFile)}
                  data-testid="chat-send-btn"
                  className="bg-slate-900 text-white p-3 hover:bg-[#0a788c] disabled:opacity-50 transition-colors"
                >
                  <Send size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* New DM dialog */}
      <Dialog open={addDMOpen} onOpenChange={setAddDMOpen}>
        <DialogContent className="max-w-md rounded-none border border-slate-200 p-0" data-testid="dm-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
            <div className="text-xs uppercase text-slate-500 font-mono">Direkt-Chat starten</div>
            <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Person auswählen</DialogTitle>
            <DialogDescription className="sr-only">Wähle eine Person aus der Klasse, um einen 1:1-Chat zu starten.</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {candidates.length === 0 ? (
              <div className="text-sm text-slate-500 italic">Keine weiteren Personen in dieser Klasse verfügbar.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {candidates.map((c) => {
                  const badge = ROLE_BADGE[c.role];
                  return (
                    <li key={c.user_id}>
                      <button
                        onClick={() => startDM(c.user_id)}
                        data-testid={`dm-target-${c.user_id}`}
                        className="w-full text-left py-3 px-1 hover:bg-slate-50 flex items-center gap-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                          {(c.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                          {badge && <span className={`text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 ${badge.cls}`}>{badge.label}</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageBubble({ m, isMe, roleBadge }) {
  const [reactions, setReactions] = useState(m.reactions || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Sync when parent messages update
  React.useEffect(() => {
    setReactions(m.reactions || []);
  }, [m.reactions]);

  const toggleReaction = async (emoji) => {
    setPickerOpen(false);
    // Optimistic update
    setReactions((prev) => {
      const existing = prev.find((r) => r.emoji === emoji);
      if (existing) {
        return prev
          .map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.me ? r.count - 1 : r.count + 1, me: !r.me }
              : r,
          )
          .filter((r) => r.count > 0);
      }
      return [...prev, { emoji, count: 1, me: true }];
    });
    try {
      const res = await api.post(`/weiterbildung/chat/messages/${m.message_id}/react`, {
        emoji,
      });
      if (res.data?.reactions) setReactions(res.data.reactions);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const bubble = isMe
    ? "bg-slate-900 text-white"
    : "bg-white border border-slate-200 text-slate-900";
  const align = isMe ? "items-end" : "items-start";
  const meta = isMe ? "text-slate-300" : "text-slate-500";
  return (
    <div className={`flex flex-col ${align} group`}>
      <div className="relative max-w-[70%]">
        <div className={`px-4 py-2.5 ${bubble}`} data-testid={`msg-${m.message_id}`}>
          {!isMe && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] font-medium">{m.sender_name}</span>
              {roleBadge && (
                <span
                  className={`text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 ${roleBadge.cls}`}
                >
                  {roleBadge.label}
                </span>
              )}
            </div>
          )}
          {m.attachment && <AttachmentView att={m.attachment} isMe={isMe} />}
          {m.content && (
            <div className={`text-sm whitespace-pre-wrap break-words ${m.attachment ? "mt-2" : ""}`}>
              {m.content}
            </div>
          )}
        </div>

        {/* React button — hover-visible */}
        <button
          onClick={() => setPickerOpen((o) => !o)}
          data-testid={`react-btn-${m.message_id}`}
          className={`absolute ${
            isMe ? "-left-8" : "-right-8"
          } top-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm hover:border-[#00CCC9] flex items-center justify-center`}
          title="Reagieren"
        >
          <SmilePlus size={13} strokeWidth={1.5} className="text-slate-500" />
        </button>

        {pickerOpen && (
          <div
            className={`absolute ${
              isMe ? "left-0" : "right-0"
            } -bottom-11 z-10 bg-white border border-slate-200 shadow-lg flex gap-0.5 p-1`}
            data-testid={`emoji-picker-${m.message_id}`}
          >
            {REACTION_EMOJIS.map((e) => (
              <button
                key={e}
                onClick={() => toggleReaction(e)}
                data-testid={`react-${e}-${m.message_id}`}
                className="w-8 h-8 hover:bg-slate-100 text-lg flex items-center justify-center transition-colors"
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Existing reaction chips */}
      {reactions.length > 0 && (
        <div className={`flex gap-1 mt-1 flex-wrap ${isMe ? "justify-end" : "justify-start"} max-w-[70%]`}>
          {reactions.map((r) => (
            <button
              key={r.emoji}
              onClick={() => toggleReaction(r.emoji)}
              data-testid={`react-chip-${r.emoji}-${m.message_id}`}
              className={`text-[11px] px-1.5 py-0.5 border flex items-center gap-1 transition-colors ${
                r.me
                  ? "border-[#00CCC9] bg-[#00CCC9]/15 text-[#0A0A0A]"
                  : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
              }`}
            >
              <span>{r.emoji}</span>
              <span className="font-mono">{r.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className={`text-[9px] mt-0.5 font-mono ${meta}`}>{formatTime(m.created_at)}</div>
    </div>
  );
}

function AttachmentView({ att, isMe }) {
  const url = `${API}/weiterbildung/chat/messages/${att.message_id}/attachment`;
  if (att.is_image) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={att.filename}
          className="max-w-[240px] max-h-[240px] object-contain border border-white/10"
          data-testid={`att-img-${att.message_id}`}
        />
      </a>
    );
  }
  const iconCls = isMe ? "text-slate-300" : "text-slate-500";
  return (
    <a
      href={url}
      target="_blank" rel="noopener noreferrer"
      className={`flex items-center gap-2 px-3 py-2 border ${isMe ? "border-white/20 bg-white/5" : "border-slate-200 bg-slate-50"} hover:opacity-90`}
      data-testid={`att-file-${att.message_id}`}
    >
      <FileText size={14} strokeWidth={1.5} className={iconCls} />
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-medium truncate ${isMe ? "text-white" : "text-slate-900"}`}>{att.filename}</div>
        <div className={`text-[9px] font-mono ${isMe ? "text-slate-300" : "text-slate-500"}`}>{Math.round((att.size || 0) / 1024)} KB</div>
      </div>
      <Download size={12} strokeWidth={1.5} className={iconCls} />
    </a>
  );
}
